<template>
  <v-container fluid class="ma-4 px-8">
    <v-row justify="start" align="center">
      <v-col cols="12" md="3" class="py-0 my-0">
        <v-select
          v-model="form.year_batch"
          :items="batches"
          outlined
          dense
          hide-details="auto"
          label="Batch"
        ></v-select>
      </v-col>
      <v-col cols="12" md="3" class="py-0 my-0">
        <v-select
          v-model="form.filter_by"
          :items="
            filterBy.filter(
              (item) =>
                item.value !== 'fee' ||
                ['Admin', 'Reviewer', 'CFO', 'Management'].includes(
                  currentUser.role
                )
            )
          "
          item-text="text"
          item-value="value"
          outlined
          dense
          hide-details="auto"
          label="Filter By"
          @change="(form.application_id = null), (form.unique_id = null)"
        ></v-select>
      </v-col>
      <v-col
        v-if="form.filter_by == 'application'"
        cols="12"
        md="3"
        class="py-0 my-0"
      >
        <v-text-field
          v-model="form.application_id"
          outlined
          dense
          hide-details="auto"
          label="Application ID"
        ></v-text-field>
      </v-col>
      <v-col v-if="form.filter_by == 'fee'" cols="12" md="3" class="py-0 my-0">
        <v-text-field
          v-model="form.usn_id"
          outlined
          dense
          hide-details="auto"
          label="USN"
        ></v-text-field>
      </v-col>
      <v-col v-else cols="12" md="3" class="py-0 my-0">
        <v-text-field
          v-model="form.unique_id"
          outlined
          dense
          hide-details="auto"
          label="Unique ID"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="3" class="py-0 my-0">
        <v-menu
          v-model="menu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="displayDate"
              readonly
              outlined
              label="Date Range"
              dense
              v-bind="attrs"
              v-on="on"
              hide-details="auto"
            ></v-text-field>
          </template>
          <v-sheet
            class="pa-4"
            elevation="4"
            style="background-color: white; border-radius: 8px"
          >
            <v-row no-gutters>
              <v-col>
                <v-date-picker
                  v-model="selectedDates"
                  range
                  @input="onDateChange"
                ></v-date-picker>
              </v-col>
              <v-col class="pl-4 pt-4" style="max-width: 150px">
                <div
                  v-for="option in dateOptions"
                  :key="option.label"
                  class="mb-2 pt-2"
                >
                  <v-btn small color="accent" @click="setQuickDate(option)">
                    {{ option.label }}
                  </v-btn>
                </div>
              </v-col>
            </v-row>
          </v-sheet>
        </v-menu>
      </v-col>
      <v-col cols="12" md="3" class="py-0 my-0">
        <v-text-field
          v-model="form.transaction_id"
          outlined
          dense
          hide-details="auto"
          label="Transaction ID"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="3" class="py-0 my-0">
        <v-select
          v-model="form.auth_status"
          :items="statuses"
          item-text="text"
          item-value="id"
          outlined
          dense
          hide-details="auto"
          label="Status"
          clearable
        ></v-select>
      </v-col>

      <v-col cols="12" md="2" class="d-flex justify-start">
        <v-btn class="accent white--text" @click="search"> Search </v-btn>
        <v-btn
          :disabled="!transactionHistory.length"
          class="accent white--text ml-2"
          @click="download"
        >
          <v-icon>mdi-download</v-icon> Download
        </v-btn>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-sheet outlined>
          <v-data-table
            :headers="transactionHistoryHeaders"
            :items="transactionHistory"
            fixed-header
            class="secondary row-height-50"
          >
            <template #[`item.auth_status`]="{ item }">
              <v-chip
                small
                :color="getAuthColor(item.auth_status)"
                class="font-weight-medium white--text"
              >
                {{ getAuthLabel(item.auth_status) }}
              </v-chip>
            </template>

            <template #[`item.amount`]="{ value }"> ₹{{ value }} </template>

            <template #[`item.transaction_date`]="{ value }">
              {{ value }}
            </template>

            <template #[`item.usn_id`]="{ item }">
              {{ item.usn_id ? item.usn_id : item.application_id }}
            </template>

            <template #[`item.payment_method_type`]="{ value }">
              {{ value.charAt(0).toUpperCase() + value.slice(1) }}
            </template>

            <template #[`item.action`]="{ item }">
              <v-chip small @click="viewTransaction(item)">View</v-chip>
            </template>

            <template #[`item.description`]="{ item }">
              <v-chip
                small
                @click="showData(item.name, item.description, 'Description')"
              >
                View
              </v-chip>
            </template>
          </v-data-table>
        </v-sheet>
      </v-col>
    </v-row>
    <v-dialog v-model="dialog" max-width="520px" persistent>
      <v-card class="px-4 rounded-xl elevation-12">
        <v-card-title class="mt-2 text-h6 font-weight-bold text-primary">
          Transaction Detail <v-spacer></v-spacer>
          <v-btn icon large @click="(dialog = false), (selectedItem = {})">
            <v-icon small color="red">mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pb-6">
          <v-simple-table dense class="text-caption">
            <template v-slot:default>
              <tbody>
                <tr>
                  <td class="font-weight-bold pr-4 pl-0">Bank Ref. No</td>
                  <td>{{ selectedItem.bank_ref_no }}</td>
                </tr>
                <tr>
                  <td class="font-weight-bold pr-4 pl-0">Bank ID</td>
                  <td>{{ selectedItem.bankid }}</td>
                </tr>
                <tr>
                  <td class="font-weight-bold pr-4 pl-0">Amount</td>
                  <td>{{ selectedItem.charge_amount }}</td>
                </tr>
                <tr>
                  <td class="font-weight-bold pr-4 pl-0">Mode</td>
                  <td>
                    {{
                      selectedItem.payment_method_type
                        ?.charAt(0)
                        .toUpperCase() +
                      selectedItem.payment_method_type?.slice(1)
                    }}
                  </td>
                </tr>
                <tr>
                  <td class="font-weight-bold pr-4 pl-0">Payment Status</td>
                  <td
                    :class="{
                      'error--text':
                        selectedItem.auth_status === '0399' ||
                        selectedItem.auth_status === 'NOT RECEIVED',
                      'warning--text': selectedItem.auth_status === '0002',
                      'success--text':
                        selectedItem.auth_status !== '0399' &&
                        selectedItem.auth_status !== '0002' &&
                        selectedItem.auth_status !== 'NOT RECEIVED',
                    }"
                  >
                    {{
                      selectedItem.auth_status === "0300"
                        ? "Success"
                        : selectedItem.auth_status === "0399"
                        ? "Failed"
                        : selectedItem.auth_status === "0002"
                        ? "Pending"
                        : selectedItem.auth_status === "NOT RECEIVED"
                        ? "Amount not received"
                        : selectedItem.auth_status
                    }}
                  </td>
                </tr>
                <tr>
                  <td class="font-weight-bold pr-4 pl-0">Transaction Status</td>
                  <td>{{ selectedItem.transaction_error_desc }}</td>
                </tr>
                <tr>
                  <td class="font-weight-bold pr-4 pl-0">Transaction ID</td>
                  <td>
                    {{ selectedItem.transactionid }}
                    <v-btn
                      icon
                      small
                      @click="copyToClipboard(selectedItem.transactionid)"
                    >
                      <v-icon small class="ml-1">mdi-content-copy</v-icon>
                    </v-btn>
                  </td>
                </tr>
                <tr>
                  <td class="font-weight-bold pr-4 pl-0">Transaction Date</td>
                  <td>
                    {{ selectedItem.transaction_date }}
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { transactionHistoryHeaders } from "@/constants/headers";

export default {
  data() {
    return {
      menu: false,
      selectedDates: [],
      displayDate: "",
      transactionHistoryHeaders,
      transactionHistory: [],
      form: {
        from_date: "",
        to_date: "",
        transaction_id: "",
        application_id: null,
        auth_status: null,
        filter_by: "application",
        payment_status: null,
        application_status: null,
      },
      statuses: [
        {
          text: "Success",
          id: "0300",
        },
        {
          text: "Failed",
          id: "0399",
        },
        // {
        //   text: "Pending",
        //   id: "0002",
        // },
        // {
        //   text: "Amount not received",
        //   id: "NOT RECEIVED",
        // },
      ],
      filterBy: [
        { text: "Application", value: "application" },
        { text: "Pre Application", value: "pre_application" },
        { text: "Fee", value: "fee" },
      ],
      dateOptions: [
        { label: "Today", days: 0 },
        { label: "Yesterday", type: "yesterday" },
        { label: "This Week", type: "week" },
        { label: "Last 7 Days", days: 6 },
        { label: "This Month", type: "month" },
        { label: "Last 30 Days", days: 29 },
        { label: "Reset", type: "reset" },
      ],
      dialog: false,
      selectedItem: {},
      batches: [],
    };
  },
  watch: {
    form: {
      handler() {
        this.transactionHistory = [];
      },
      deep: true,
    },
  },
  computed: {
    currentUser() {
      return this.$store.state.currentUser;
    },
  },
  async created() {
    if (this.currentUser.partner_payment_required)
      this.$router.push("/bulk_payment_report");
    this.$store.commit("setBreadcrumbs", [{ text: "Transaction History" }]);
    await this.generateBatchList();
    await this.search();
  },
  methods: {
    onDateChange() {
      if (this.selectedDates.length === 2) {
        const [fromDate, toDate] = this.selectedDates;
        this.form.from_date = this.formatDateBackend(fromDate);
        this.form.to_date = this.formatDateBackend(toDate);
        this.displayDate = this.formatDisplayDate(fromDate, toDate);
      }
    },
    setQuickDate(option) {
      const today = new Date();
      let fromDate = new Date(today);
      let toDate = new Date(today);

      if (option.type === "reset") {
        this.form.from_date = null;
        this.form.to_date = null;
        this.displayDate = "";
        this.selectedDates = [];
        return;
      }

      if (option.type === "week") {
        fromDate.setDate(today.getDate() - today.getDay());
      } else if (option.type === "month") {
        fromDate.setDate(1);
      } else if (option.type === "yesterday") {
        fromDate.setDate(today.getDate() - 1);
        toDate.setDate(today.getDate() - 1);
      } else if (option.days != null) {
        fromDate.setDate(today.getDate() - option.days);
      }

      this.selectedDates = [fromDate, toDate];
      this.onDateChange();
    },
    async search() {
      this.transactionHistoryHeaders =
        this.form.filter_by === "pre_application"
          ? transactionHistoryHeaders
          : transactionHistoryHeaders.filter(
              (header) =>
                header.value !== "email" && header.value !== "unique_id"
            );

      if (this.form.filter_by === "fee") {
        let updatedHeaders = JSON.parse(
          JSON.stringify(this.transactionHistoryHeaders)
        );

        const appIdHeader = updatedHeaders.find(
          (header) => header.value === "application_id"
        );

        if (appIdHeader) {
          appIdHeader.value = "application_id";
          appIdHeader.text = "USN";
        }
        this.transactionHistoryHeaders = updatedHeaders;
      }

      const queryString = this.objectToQueryParams({
        ...this.form,
        transaction_id: this.form.transaction_id.toUpperCase(),
        filter_by:
          this.form.application_id !== null ? null : this.form.filter_by,
      });

      try {
        const res = await this.$axios.get(`/billdesk_payments?${queryString}`);
        this.transactionHistory = res.data.data.sort((a, b) => {
          const getTime = (dateStr) => {
            const [datePart, timePart] = dateStr.split(" ");
            const [day, month, year] = datePart.split("-");
            return new Date(`${year}-${month}-${day}T${timePart}`).getTime();
          };

          return getTime(b.transaction_date) - getTime(a.transaction_date);
        });
      } catch (error) {
        console.error("API Error:", error);
      }
    },
    objectToQueryParams(obj) {
      if (
        this.currentUser.role === "Business" ||
        this.currentUser.role === "Business Reviewer"
      ) {
        obj.partner_id = this.currentUser.partner_id;
      }
      if (obj.year_batch) {
        const [year, batch] = obj.year_batch.split("-");
        obj.year = parseInt(year, 10);
        obj.batch = parseInt(batch, 10);
        delete obj.year_batch;
      }
      if (this.form.filter_by == "fee") {
        delete obj.batch;
        delete obj.year;
      }
      return Object.keys(obj)
        .filter(
          (key) =>
            obj[key] !== undefined && obj[key] !== null && obj[key] !== ""
        )
        .map(
          (key) => `${encodeURIComponent(key)}=${encodeURIComponent(obj[key])}`
        )
        .join("&");
    },
    formatDateBackend(date) {
      const d = new Date(date);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        "0"
      )}-${String(d.getDate()).padStart(2, "0")}`;
    },
    formatDisplayDate(fromDate, toDate) {
      const format = (d) =>
        `${String(d.getDate()).padStart(2, "0")}-${String(
          d.getMonth() + 1
        ).padStart(2, "0")}-${d.getFullYear()}`;
      return `${format(new Date(fromDate))} ~ ${format(new Date(toDate))}`;
    },
    getAuthLabel(code) {
      return (
        {
          "0300": "Success",
          "0399": "Failed",
          "0002": "Pending",
          "NOT RECEIVED": "Amount not received",
        }[code] || code
      );
    },
    getAuthColor(code) {
      return code === "0399" || code === "NOT RECEIVED"
        ? "red"
        : code === "0002"
        ? "yellow"
        : "green";
    },
    viewTransaction(item) {
      this.selectedItem = item;
      this.dialog = true;
    },
    copyToClipboard(text) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          this.$store.commit("showSnackbar", {
            message: "Copied to clipboard",
            color: "success",
          });
        })
        .catch((err) => {
          this.$store.commit("showSnackbar", {
            message: "Failed to copy",
            color: "error",
          });
        });
    },
    async download() {
      this.$axios
        .get("/download_transaction_history", {
          params: {
            ...this.form,
            transaction_id: this.form.transaction_id.toUpperCase(),
            filter_by:
              this.form.application_id !== null ? null : this.form.filter_by,
          },
          responseType: "blob",
        })
        .then((res) => {
          let blob = new Blob([res.data], { type: "text/csv" }),
            url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "Transaction History.csv");
          document.body.appendChild(link);
          link.click();
        });
    },
    async generateBatchList() {
      try {
        const response = await this.$axios.get("/get_year_batch");
        this.batches = response.data.data;
        this.form.year_batch = this.batches[0];
      } catch (error) {
        console.log(error);
      }
    },
  },
};
</script>
